import { useInfiniteQuery } from "@tanstack/react-query";
import { useCallback, useEffect, useRef, useState } from "react";
import api from "../../api";
import { createError, HTTPErrors } from "../../errors";
import { getNextPageParam } from "../../helpers/ReactQueryHelper";
import ApiResponse from "../../interfaces/ApiResponse";

// TODO: OpenAPI spec が更新されたら boronClient.GET() + 自動生成型に切り替える
const path = "/api/v1/sections/multi_section_batches/drills";

const MIN_BATCH_SIZE = 20;

export type DrillLearningMaterial = {
  name: string;
  steakMaterialCode: string;
  drillRegistrationCode: string;
  sectionCount: number;
  imageUrl: string;
  updatedAt: string;
};

type ResponseBody = {
  drills: ApiResponse<DrillLearningMaterial[]>;
};

type PageData = ApiResponse<DrillLearningMaterial[]>;

type UseDrillLearningMaterialsProps = {
  name?: string;
};

export const useDrillLearningMaterials = (
  props?: UseDrillLearningMaterialsProps,
) => {
  const name = props?.name;
  const {
    data,
    isLoading,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
    error,
  } = useInfiniteQuery<PageData, HTTPErrors>({
    queryKey: ["drillLearningMaterials", "list", name],
    queryFn: async ({ pageParam }) => {
      const response = await api.interruptGet(path, {
        query: { page: pageParam as number, name },
      });

      if (response.ok) {
        const json: ResponseBody = await response.json();
        return json.drills;
      }

      throw await createError(response);
    },
    initialPageParam: 1,
    getNextPageParam,
    gcTime: 0,
  });

  const batchFetchingRef = useRef(false);

  const fetchBatch = useCallback(async () => {
    if (batchFetchingRef.current) {
      return;
    }
    batchFetchingRef.current = true;
    try {
      let accumulated = 0;
      let result = await fetchNextPage();
      accumulated +=
        result.data?.pages[result.data.pages.length - 1]?.data.length ?? 0;

      while (accumulated < MIN_BATCH_SIZE && result.hasNextPage) {
        result = await fetchNextPage();
        accumulated +=
          result.data?.pages[result.data.pages.length - 1]?.data.length ?? 0;
      }
    } finally {
      batchFetchingRef.current = false;
    }
  }, [fetchNextPage]);

  const materials = data?.pages.flatMap((page) => page.data) ?? [];

  // 検索語が変わったらバッチ取得の初期化状態をリセット
  const [initialBatchDone, setInitialBatchDone] = useState(false);
  useEffect(() => {
    setInitialBatchDone(false);
  }, [name]);

  // 初回ロード後、アイテム数が MIN_BATCH_SIZE 未満なら自動でバッチ取得を開始
  useEffect(() => {
    if (isLoading || initialBatchDone) {
      return;
    }
    if (materials.length < MIN_BATCH_SIZE && hasNextPage) {
      fetchBatch().then(() => setInitialBatchDone(true));
    } else {
      setInitialBatchDone(true);
    }
  }, [isLoading, initialBatchDone, materials.length, hasNextPage, fetchBatch]);

  return {
    materials,
    isLoading: isLoading || !initialBatchDone,
    hasNextPage,
    fetchBatch,
    isFetchingNextPage,
    error,
  };
};
